/**
 * TRAVEL-WINDOW fast lane (ADR-011, P3.md 5b).
 *
 * Two halves, deliberately asymmetric:
 *
 *  - COMPUTE (sync/edit time, `computeTravelWindow`): walk local gig states,
 *    resolve every travel leg's venue-local wall times to absolute instants
 *    (Intl-based — no tz library, no network), and persist a fully
 *    denormalized `meta.travel_window` snapshot. All decisions happen HERE.
 *
 *  - BOOT (pre-hydration, `readTravelWindow` + `activeLegs`): one IndexedDB
 *    read + plain number compares against Date.now(). Zero network, zero
 *    auth, zero registry parsing on this path — main.tsx renders the Travel
 *    Card from the snapshot FIRST and lets the app hydrate behind it.
 *
 * A leg is "in a travel window" when now ∈ [dep − 24h, max(dep, arr) + 6h].
 * Arrival may precede departure on the wall clock (dateline legs — "sometimes
 * we timetravel"): the window end uses max(dep, arr), never trusting arr > dep.
 */
import { registry } from "@td/registry";
import { isItemDeleted, type Item } from "@td/sync-core";
import {
  openLocalDb,
  type GigRecord,
  type LocalStore,
  type TravelWindowLeg,
  type TravelWindowMeta,
} from "../db/idb.ts";

export const WINDOW_BEFORE_DEP_MS = 24 * 60 * 60 * 1000; // −24h
export const WINDOW_AFTER_ARR_MS = 6 * 60 * 60 * 1000; // +6h

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function s(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Offset of `tz` from UTC at the instant `utcMs`, in ms. */
function tzOffsetMs(utcMs: number, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(utcMs))) parts[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(parts["year"]),
    Number(parts["month"]) - 1,
    Number(parts["day"]),
    Number(parts["hour"]) % 24, // "24" at midnight in some ICU versions
    Number(parts["minute"]),
    Number(parts["second"]),
  );
  return asUtc - utcMs;
}

/**
 * Resolve a venue-local wall time (ADR-006: all domain times are wall times)
 * to an absolute epoch in the given IANA tz. Two-pass offset resolution
 * handles DST edges; an invalid/empty tz falls back to the device zone.
 * Returns null when day/time are absent or malformed (never guesses).
 */
export function zonedEpochMs(day: string, time: string, tz: string): number | null {
  if (!DAY_RE.test(day) || !TIME_RE.test(time)) return null;
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const [h, mi] = time.split(":").map(Number) as [number, number];
  try {
    const guess = Date.UTC(y, m - 1, d, h, mi);
    const off1 = tzOffsetMs(guess, tz);
    const off2 = tzOffsetMs(guess - off1, tz);
    return guess - off2;
  } catch {
    // Unknown tz string — device-local interpretation (honest fallback).
    return new Date(y, m - 1, d, h, mi).getTime();
  }
}

/** Whole-day difference between two YYYY-MM-DD strings (b − a); 0 on gaps. */
export function dayOffset(a: string, b: string): number {
  if (!DAY_RE.test(a) || !DAY_RE.test(b)) return 0;
  const [ay, am, ad] = a.split("-").map(Number) as [number, number, number];
  const [by, bm, bd] = b.split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Gig display name + tz come from the gig header item (entity spec lands with
 * the P3 server gig-entity work; these field names are gig-level, not
 * registry collections — read tolerantly, never invent). */
function gigHeaderField(gig: GigRecord, key: string): string {
  return s(gig.state.gig?.fields[key]);
}

/**
 * Compute the travel-window snapshot from local gig states. Returns null when
 * no leg is in (or within `horizonMs` of) a window — the boot check then skips
 * the fast lane entirely. Sorted soonest-departure first.
 */
export function computeTravelWindow(
  gigs: readonly GigRecord[],
  nowMs: number,
  deviceTz: string = deviceTimeZone(),
): TravelWindowMeta | null {
  const travelKey = registry.travel.key;
  const f = registry.travel.fields;
  const legs: (TravelWindowLeg & { gig_name: string; tz: string })[] = [];

  for (const gig of gigs) {
    const tz = gigHeaderField(gig, "timezone") || deviceTz;
    const gigName = gigHeaderField(gig, "name") || gigHeaderField(gig, "title") || gig.gig_id;
    const items = gig.state.collections[travelKey] ?? {};
    for (const [itemId, item] of Object.entries(items) as [string, Item][]) {
      if (isItemDeleted(item)) continue;
      // ADR-006: each endpoint's wall time lives in ITS OWN zone — dep_tz /
      // arr_tz when set, else the gig's timezone. A Paris→Mexico City departure
      // resolved in CDMX tz would fire the fast lane ~7h off (2026-07-02 review).
      const depTz = s(item.fields[f.dep_tz.key]) || tz;
      const arrTz = s(item.fields[f.arr_tz.key]) || tz;
      const depDay = s(item.fields[f.dep_day.key]);
      const depTime = s(item.fields[f.dep_time.key]);
      const dep = zonedEpochMs(depDay, depTime, depTz);
      if (dep === null) continue; // no departure instant, no window — never guess
      const arrDay = s(item.fields[f.arr_day.key]) || depDay;
      const arrTime = s(item.fields[f.arr_time.key]);
      const arr = zonedEpochMs(arrDay, arrTime, arrTz);
      // Dateline/red-eye safety: the window END never precedes the departure.
      const windowEnd = Math.max(dep, arr ?? dep) + WINDOW_AFTER_ARR_MS;
      const windowStart = dep - WINDOW_BEFORE_DEP_MS;
      if (nowMs > windowEnd) continue; // already past — irrelevant to any boot
      legs.push({
        gig_id: gig.gig_id,
        item_id: itemId,
        kind: s(item.fields[f.kind.key]),
        dep_place: s(item.fields[f.dep_place.key]),
        arr_place: s(item.fields[f.arr_place.key]),
        dep_iata: s(item.fields[f.dep_iata.key]),
        arr_iata: s(item.fields[f.arr_iata.key]),
        dep_day: depDay,
        dep_time: depTime,
        arr_day: arrDay,
        arr_time: arrTime,
        day_offset: dayOffset(depDay, arrDay),
        carrier: s(item.fields[f.carrier.key]),
        ref: s(item.fields[f.ref.key]),
        seat: s(item.fields[f.seat.key]),
        gate: s(item.fields[f.gate.key]),
        terminal: s(item.fields[f.terminal.key]),
        carriage: s(item.fields[f.carriage.key]),
        platform: s(item.fields[f.platform.key]),
        person_id: s(item.fields[f.person_id.key]),
        dep_epoch_ms: dep,
        arr_epoch_ms: arr,
        window_start_ms: windowStart,
        window_end_ms: windowEnd,
        gig_name: gigName,
        tz,
      });
    }
  }

  if (legs.length === 0) return null;
  legs.sort((a, b) => a.dep_epoch_ms - b.dep_epoch_ms);
  const head = legs[0]!;
  return {
    computed_at: nowMs,
    tz: head.tz,
    gig_name: head.gig_name,
    legs: legs.map(({ gig_name: _g, tz: _t, ...leg }) => leg),
  };
}

/**
 * The boot-time check: which stored legs are inside their window right now.
 * `personId` (when known — share-key viewer sessions persist theirs) keeps a
 * leg only if it is that person's own or a whole-party leg (empty person_id):
 * the full-screen wake-locked card must never open on someone ELSE's flight.
 * NOTE(SNAPSHOT, revisit-when-smarter): owner/editor sessions pass null — the
 * data model has no signed-in-user ↔ crew-row link yet, so the owner's boot
 * still considers every leg; wire this filter to that link when it lands.
 */
export function activeLegs(
  meta: TravelWindowMeta | null,
  nowMs: number,
  personId: string | null = null,
): TravelWindowLeg[] {
  if (meta === null) return [];
  return meta.legs.filter(
    (l) =>
      nowMs >= l.window_start_ms &&
      nowMs <= l.window_end_ms &&
      (personId === null || l.person_id === "" || l.person_id === personId),
  );
}

/**
 * Recompute + persist the snapshot. Called after every repaint-worthy change
 * (delta pulls AND local edits both funnel through App.repaint) — the record
 * is always as fresh as the last time the app was open, which is exactly the
 * data an offline boot can ever have.
 */
export async function refreshTravelWindow(store: LocalStore, nowMs: number = Date.now()): Promise<void> {
  const gigs = await store.listGigs();
  await store.putTravelWindow(computeTravelWindow(gigs, nowMs));
}

/**
 * PRE-HYDRATION read (the hot path): one IDB open + one meta get. Any failure
 * returns null — the boot falls through to the normal app, never crashes.
 */
export async function readTravelWindow(): Promise<TravelWindowMeta | null> {
  try {
    const store = await openLocalDb();
    return await store.getTravelWindow();
  } catch {
    return null;
  }
}
